import type {SpanNode} from '@github-ui/copilot-chat-state/tracing'
import isEmpty from 'lodash-es/isEmpty'

export interface DecoratedNode extends SpanNode {
  duration: number
  depth: number
  row: number
  children: DecoratedNode[]
}

/**
 * Same as DecoratedNode, but with string fields instead of number to work around Primer DataTable sorting bugs.
 */
export type StringyNode = Omit<DecoratedNode, 'startTime' | 'endTime' | 'duration' | 'children' | 'row'> & {
  startTime: string
  endTime: string
  duration: string
  row: string
  children: StringyNode[]
  hasData: string
}

/**
 * Adds depth field to each node and calculates min and max times for the entire tree.
 * And adds duration field to each node. Row is initially set to depth but will be
 * recalculated by assignRowsToTree to handle parallel spans.
 */
export function decorateTree(
  node: Readonly<SpanNode>,
  depth = 0,
): {node: DecoratedNode; minTime: number; maxTime: number; maxDepth: number} {
  const startTime = node.startTime
  const endTime = node.endTime
  let minTime = startTime
  let maxTime = endTime
  let maxDepth = depth

  const decoratedChildren: DecoratedNode[] = node.children.map(child => {
    const result = decorateTree(child, depth + 1)
    minTime = Math.min(minTime, result.minTime)
    maxTime = Math.max(maxTime, result.maxTime)
    maxDepth = Math.max(maxDepth, result.maxDepth)
    return result.node
  })

  const decoratedNode: DecoratedNode = {
    ...node,
    startTime,
    endTime,
    duration: endTime - startTime,
    depth,
    row: depth, // Initially same as depth, recalculated by assignRowsToTree
    children: decoratedChildren,
  }

  return {node: decoratedNode, minTime, maxTime, maxDepth}
}

/**
 * Assigns row positions to nodes in the tree, stacking parallel/overlapping spans
 * on different rows to prevent visual overlap.
 * Returns the updated tree and the maximum row used.
 */
export function assignRowsToTree(root: DecoratedNode): {node: DecoratedNode; maxRow: number} {
  return assignRowsRecursive(root, 0)
}

function assignRowsRecursive(node: DecoratedNode, minRow: number): {node: DecoratedNode; maxRow: number} {
  const updatedNode: DecoratedNode = {...node, row: minRow}

  if (node.children.length === 0) {
    return {node: updatedNode, maxRow: minRow}
  }

  // Sort children by start time for lane assignment, but track original indices
  const childrenWithIndices = node.children.map((child, index) => ({child, originalIndex: index}))
  const sortedChildren = [...childrenWithIndices].sort((a, b) => a.child.startTime - b.child.startTime)

  // Track lanes: each entry is {endTime, maxRowUsed}
  // A lane represents a horizontal "track" where non-overlapping spans can be placed
  const lanes: Array<{endTime: number; maxRowUsed: number}> = []
  const assignedChildren: Array<{node: DecoratedNode; originalIndex: number}> = []
  let overallMaxRow = minRow

  let lanesMaxRowUsed = lanes.reduce((max, lane) => Math.max(max, lane.maxRowUsed), minRow)

  for (const {child, originalIndex} of sortedChildren) {
    // Find the first lane where this span fits (no time overlap)
    let assignedLaneIndex = -1
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i]

      if (isEmpty(lane)) {
        throw new Error(`Lane at index '${i}' should not be empty.`)
      }

      if (lane.endTime <= child.startTime) {
        assignedLaneIndex = i
        break
      }
    }

    let childMinRow: number
    if (assignedLaneIndex === -1) {
      // No existing lane fits - create a new one below all existing lanes
      if (lanes.length === 0) {
        childMinRow = minRow + 1
      } else {
        // New lane starts after the deepest point of all existing lanes
        childMinRow = lanesMaxRowUsed + 1
      }
      assignedLaneIndex = lanes.length
    } else {
      // Reuse an existing lane
      childMinRow = minRow + 1 + assignedLaneIndex
    }

    // Recursively assign rows to child's subtree
    const result = assignRowsRecursive(child, childMinRow)
    assignedChildren.push({node: result.node, originalIndex})

    // Update or create lane info
    if (assignedLaneIndex >= lanes.length) {
      lanes.push({endTime: child.endTime, maxRowUsed: result.maxRow})
      lanesMaxRowUsed = Math.max(lanesMaxRowUsed, result.maxRow)
    } else {
      const assignedLane = lanes[assignedLaneIndex]

      if (isEmpty(assignedLane)) {
        throw new Error(`No assigned lane is defined for the index '${assignedLaneIndex}'.`)
      }

      const updatedMaxRowUsed = Math.max(assignedLane.maxRowUsed, result.maxRow)
      lanes[assignedLaneIndex] = {
        endTime: child.endTime,
        maxRowUsed: updatedMaxRowUsed,
      }
      lanesMaxRowUsed = Math.max(lanesMaxRowUsed, updatedMaxRowUsed)
    }

    overallMaxRow = Math.max(overallMaxRow, result.maxRow)
  }

  // Restore original order
  const orderedChildren = new Array<DecoratedNode>(assignedChildren.length)
  for (const {node: childNode, originalIndex} of assignedChildren) {
    orderedChildren[originalIndex] = childNode
  }

  updatedNode.children = orderedChildren
  return {node: updatedNode, maxRow: overallMaxRow}
}

/**
 * Subtracts minTime from startTime and endTime of each node in the tree.
 */
export function fixTimes(node: DecoratedNode, minTime: number, maxTime: number): DecoratedNode {
  const fixedChildren = node.children.map(child => fixTimes(child, minTime, maxTime))
  return {
    ...node,
    startTime: node.startTime - minTime,
    endTime: node.endTime - minTime,
    children: fixedChildren,
  }
}

export function stringifyTree(node: DecoratedNode): StringyNode {
  return {
    ...node,
    startTime: node.startTime.toString(),
    endTime: node.endTime.toString(),
    duration: node.duration.toString(),
    row: node.row.toString(),
    children: node.children.map(stringifyTree),
    hasData: hasLLMTracerData(node) ? 'Yes' : 'No',
  }
}

/**
 * Checks if a span has LLMTracer data (sessionId, userId, input, output, or metadata).
 */
export function hasLLMTracerData(node: DecoratedNode | StringyNode): boolean {
  return !!(node.sessionId || node.userId || node.input || node.output || node.metadata)
}

/**
 * Filters a tree to only include spans with LLMTracer data and their ancestors.
 * In verbose mode, returns the original tree.
 */
export function filterTreeByVerbosity(node: DecoratedNode, verboseMode: boolean): DecoratedNode | null {
  if (verboseMode) {
    return node
  }

  // Recursively filter children
  const filteredChildren = node.children
    .map(child => filterTreeByVerbosity(child, verboseMode))
    .filter((child): child is DecoratedNode => child !== null)

  // Include this node if it has LLM data or if any of its children are included
  const shouldInclude = hasLLMTracerData(node) || filteredChildren.length > 0

  if (shouldInclude) {
    return {
      ...node,
      children: filteredChildren,
    }
  }

  return null
}

export interface IDNode extends StringyNode {
  id: string
}

function makeList(root: StringyNode): IDNode[] {
  const list: IDNode[] = []

  function traverse(node: StringyNode) {
    list.push({...node, id: node.spanId})
    for (const child of node.children) {
      traverse(child)
    }
  }
  traverse(root)

  return list
}

/**
 * Computes both full and filtered tree lists for the TraceList component.
 */
export function computeTreeLists(decoratedTreeData: {
  node: DecoratedNode
  minTime: number
  maxTime: number
  maxDepth: number
}): {
  fullList: IDNode[]
  shortList: IDNode[]
} {
  // Generate verbose tree
  const fixedTree = fixTimes(decoratedTreeData.node, decoratedTreeData.minTime, decoratedTreeData.maxTime)
  const stringifiedTree = stringifyTree(fixedTree)
  const fullList = makeList(stringifiedTree)

  // Generate filtered tree
  const filtered = filterTreeByVerbosity(decoratedTreeData.node, false)
  let shortList: IDNode[] = []
  if (filtered) {
    const fixedFiltered = fixTimes(filtered, decoratedTreeData.minTime, decoratedTreeData.maxTime)
    const stringifiedFiltered = stringifyTree(fixedFiltered)
    shortList = makeList(stringifiedFiltered)
  }

  return {
    fullList,
    shortList,
  }
}
